import { atom } from "jotai";
import { JotaiTool } from "../../../../index";

export const authenticationOperationsToolsAtom = atom<JotaiTool<any>[]>(() => [
  // TODO: Add specific tools for authentication operations
  // Example: get(authenticationCheckAtom), get(choicesApiAtom), get(createAuthAtom), etc.
]);
authenticationOperationsToolsAtom.debugLabel =
  "authenticationOperationsToolsAtom";

export const authenticationOperationsRecipe = `# Authentication Operations: Managing app connections and authentication

## CRITICAL SEQUENCE REQUIREMENT
**This is CHECKPOINT 1/3 in the workflow sequence.**

🚫 **HARD STOP** - User MUST confirm authentication before proceeding to testing (CHECKPOINT 2/3).

**MANDATORY PAUSE AFTER AUTH SETUP:**
After completing authentication setup, you MUST use this exact message format:

"Authentication setup complete. **CHECKPOINT 1/3** - Please test the connection in your Zapier account and confirm it's working. Reply 'auth confirmed' when ready for me to proceed to testing."

⛔ **DO NOT PROCEED** to testing, data collection, or field mapping until user confirms authentication is working.

## Overview
Handle authentication workflows, connection management, and troubleshooting for Zapier app integrations. This includes checking auth status, handling auth errors, and managing different authentication types within Canvas workflows.

## Key API Endpoints

### GET authentications/authentications/v1/authentications
- **Purpose**: List all user authentications with filtering options
- **Authentication**: Uses JWT tokens via network_request tool
- **Example**: \`network_request({ method: "GET", api: "authentications", path: "/authentications/v1/authentications" })\`

### GET authentications/authentications/v1/authentications/{id}
- **Purpose**: Get specific authentication details by ID
- **Authentication**: Uses JWT tokens via network_request tool
- **Example**: \`network_request({ method: "GET", api: "authentications", path: "/authentications/v1/authentications/{id}" })\`

### POST authentications/authentications/v1/authentications/create
- **Purpose**: Create new app authentication connection
- **Authentication**: Uses JWT tokens via network_request tool
- **Example**: \`network_request({ method: "POST", api: "authentications", path: "/authentications/v1/authentications/create" })\`

### PATCH authentications/authentications/v1/authentications/{id}
- **Purpose**: Update authentication properties and settings
- **Authentication**: Uses JWT tokens via network_request tool
- **Example**: \`network_request({ method: "PATCH", api: "authentications", path: "/authentications/v1/authentications/{id}" })\`

### DELETE authentications/authentications/v1/authentications/{id}
- **Purpose**: Delete/disconnect an app authentication
- **Authentication**: Uses JWT tokens via network_request tool
- **Example**: \`network_request({ method: "DELETE", api: "authentications", path: "/authentications/v1/authentications/{id}" })\`

### POST authentications/authentications/v1/authentications/{id}/test
- **Purpose**: Test authentication connection to verify it's working
- **Authentication**: Uses JWT tokens via network_request tool
- **Example**: \`network_request({ method: "POST", api: "authentications", path: "/authentications/v1/authentications/{id}/test" })\`

### GET authentications/authentications/v1/authentications/{id}/data
- **Purpose**: Retrieve authentication data and connection details
- **Authentication**: Uses JWT tokens via network_request tool
- **Example**: \`network_request({ method: "GET", api: "authentications", path: "/authentications/v1/authentications/{id}/data" })\`

## Implementation Steps (CHECKPOINT 1/3 SEQUENCE)

**SEQUENCE RULE**: Complete ALL steps below, then PAUSE for user confirmation before proceeding to CHECKPOINT 2/3.

1. **List existing authentications** using GET /authentications/v1/authentications to see available connections
2. **Test auth status** using POST /authentications/v1/authentications/{id}/test for specific connections
3. **Handle auth errors** by providing clear error messages and reconnection flows
4. **Create new auth** using POST /authentications/v1/authentications/create when no valid connection exists
5. **Update Canvas state** with authentication status and connection details
6. **Implement retry logic** for temporary auth failures
7. **Provide user feedback** for auth success/failure states
8. **Monitor auth expiration** and proactive renewal when possible

**🚫 MANDATORY PAUSE POINT 🚫**
After completing steps 1-8, you MUST:

✅ **STOP execution**
✅ **Ask user to verify** authentication is working in their Zapier account  
✅ **WAIT for confirmation** - Do not proceed until user replies "auth confirmed" or similar
✅ **BLOCK all other operations** - No testing, no field mapping, no data collection until confirmed

**CHECKPOINT 1/3 SUCCESS CRITERIA:**
- Authentication connection established ✓
- Connection tested and working ✓  
- User has verified and confirmed auth ✓
- Ready to proceed to CHECKPOINT 2/3 (Testing)

## Critical Patterns

### Authentication Check Structure
\`\`\`
{
  app: 'app_slug',
  auth_id: 'auth_connection_id',
  test_method: 'GET'|'POST',
  test_url: '/api/test/endpoint'
}
\`\`\`

### Auth Error Handling
\`\`\`
{
  error_type: 'auth_failed'|'connection_expired'|'invalid_credentials',
  error_message: 'Human readable error',
  suggested_action: 'reconnect'|'reauthorize'|'contact_support',
  auth_url: 'https://zapier.com/app/connections/...'
}
\`\`\`

### Connection Status Response
\`\`\`
{
  status: 'valid'|'invalid'|'expired'|'testing',
  auth_id: 'connection_identifier',
  app_name: 'Application Name',
  last_tested: 'ISO_timestamp',
  expires_at: 'ISO_timestamp'
}
\`\`\`

## Authentication Types

### OAuth 2.0
- **Flow**: Redirect-based authorization with refresh tokens
- **Expiration**: Automatic token refresh when possible
- **Errors**: Handle scope changes and authorization revocation
- **Testing**: Verify OAuth scopes and permissions

### API Key
- **Format**: Key-based authentication with headers/query params
- **Validation**: Direct API testing for key validity
- **Errors**: Invalid key, rate limiting, permission issues
- **Security**: Secure key storage and transmission

### Session-based
- **Cookies**: Session management through web interface
- **Expiration**: Handle session timeouts gracefully
- **Testing**: Validate session state and permissions
- **Renewal**: Automatic login flow when possible

## Important Notes

- **Always test auth before using** in workflows to prevent runtime failures
- **Cache auth status** temporarily to avoid excessive API calls
- **Provide clear error messages** to help users understand auth issues
- **Handle rate limits** on auth-check endpoints appropriately
- **Monitor auth expiration** to proactively prompt renewals
- **Secure credential storage** - never log or expose auth tokens
- **Support multiple accounts** for apps that allow multiple connections

## Error Handling Patterns

### Connection Failed
- Check network connectivity
- Verify API endpoint availability
- Provide retry mechanism with exponential backoff

### Invalid Credentials
- Clear error messaging about credential issues
- Direct user to reconnection flow
- Avoid exposing sensitive credential details

### Expired Authentication
- Automatic refresh when possible
- Graceful fallback to manual reauthorization
- Preserve user work during reauth process

### Rate Limiting
- Implement proper backoff strategies
- Queue auth requests when necessary
- Inform users about temporary delays

## Troubleshooting

- **401 Unauthorized**: Credential issues - prompt for reconnection
- **403 Forbidden**: Permission/scope issues - check required permissions
- **429 Rate Limited**: Too many requests - implement backoff strategy
- **500 Server Error**: Service issues - retry with exponential backoff
- **Network timeout**: Connectivity issues - check internet connection
- **Auth not found**: Connection deleted - prompt for new connection setup
- **Scope changes**: App permissions changed - request reauthorization`;
